import React, { useState } from 'react';
import { NewModal } from 'lib/components';
import { Gap } from 'lib/components/styles/layout';
import { Formik, Form } from 'formik';
import { ParagraphNormal } from 'lib/components/styles/typography';
import { Button } from 'react-covideo-common';
import {
  IDeleteTemplateRequestValues,
  useDeleteLandingPageDesignMutation,
} from 'lib/api/superadmin/landingPageDesigns/useDeleteLandingPageDesignMutation';
import {
  ILandingPageDesignsQueryParams,
  LandingPageDesignAccess,
  useInfiniteQueryLandingPageDesigns,
} from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPageDesigns';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import {
  DELETE_AND_REPLACE_TEXT,
  DELETE_TEMPLATE_TEXT,
  ACTIVE_TEMPLATE_INFO,
  INACTIVE_TEMPLATE_INFO,
  DEFAULT_DESIGNS_SIZE,
} from '../constants';

interface IProps {
  templateId: number;
  closeModalHandler: () => void;
  accessList: LandingPageDesignAccess[];
}

export const DeleteLandingPageDesignModal = ({
  templateId,
  closeModalHandler,
  accessList,
}: IProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const isActive = !!accessList.length;

  const initialFormikValues: IDeleteTemplateRequestValues = {
    templateId,
    replacementLandingPageDesignId: undefined,
  };

  const { mutateAsync, isLoading: isDeleting } =
    useDeleteLandingPageDesignMutation({
      closeModalHandler,
    });

  const lpDesignsInfiniteQueryParams: ILandingPageDesignsQueryParams = {
    search: searchQuery,
    limit: DEFAULT_DESIGNS_SIZE,
  };

  const {
    data: landingPageDesignsInfinite,
    fetchNextPage,
    isFetching: isFetchingLandingPageDesigns,
  } = useInfiniteQueryLandingPageDesigns(
    lpDesignsInfiniteQueryParams,
    isActive
  );

  const totalCount = landingPageDesignsInfinite?.pages?.[0]?.count ?? 0;

  const pageTotal =
    landingPageDesignsInfinite?.pages?.reduce((acc, page) => {
      return acc + (page?.data?.length || 0);
    }, 0) || 0;

  const landingPageDesignsPages = landingPageDesignsInfinite?.pages ?? [];

  const lpDesignsDropdownOptions = Array.from(
    new Map(
      landingPageDesignsPages
        .flatMap(page => page.data)
        .filter(design => design.id !== templateId) // filter out the current template
        .map(design => [design.id, { label: design.title, value: design.id }])
    ).values()
  );

  const onSubmitHandler = async (values: IDeleteTemplateRequestValues) => {
    const { templateId, replacementLandingPageDesignId } = values;
    // check for empty string since isClearable returns an empty string
    const replacementId =
      replacementLandingPageDesignId === ''
        ? undefined
        : replacementLandingPageDesignId;

    await mutateAsync({
      templateId,
      replacementLandingPageDesignId: replacementId,
    });
  };

  const deleteText = isActive ? DELETE_AND_REPLACE_TEXT : DELETE_TEMPLATE_TEXT;
  const infoText = isActive ? ACTIVE_TEMPLATE_INFO : INACTIVE_TEMPLATE_INFO;

  const isLoading = isFetchingLandingPageDesigns || isDeleting;

  return (
    <NewModal
      headerText='You are about to delete a template'
      closeModal={closeModalHandler}
      style={{
        content: { width: 592, maxWidth: 592 },
      }}
    >
      <Formik initialValues={initialFormikValues} onSubmit={onSubmitHandler}>
        {({ handleSubmit }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Gap
                flexDirection='column'
                justifyContent='flex-start'
                alignItems='flex-start'
                m='0 0 16px 0'
              >
                <ParagraphNormal>{infoText}</ParagraphNormal>
              </Gap>

              {isActive && (
                <FormikDropdownField
                  name={`replacementLandingPageDesignId`}
                  options={lpDesignsDropdownOptions}
                  isSearchable
                  isClearable
                  isLoading={isLoading}
                  onSearchInputChange={setSearchQuery}
                  onMenuScrollToBottom={() => {
                    if (pageTotal < totalCount) {
                      fetchNextPage();
                    }
                  }}
                  hideError
                />
              )}

              <Gap
                m='32px 0 0 0'
                alignItems='center'
                justifyContent='flex-end'
                gap='12px'
              >
                <Button
                  disabled={isActive ? isLoading : isDeleting}
                  text={deleteText}
                  variant='primary'
                  type='submit'
                />
              </Gap>
            </Form>
          );
        }}
      </Formik>
    </NewModal>
  );
};
